import { useState } from 'react'
import {Copy, ChevronDown, ChevronUp, Wand2} from 'lucide-react'
import toast from 'react-hot-toast'
import { copiarTexto } from '@/utils/clipboard'
import {
  obterAvisoSeguranca,
  resolverProtocolos,
  resolverChave,
  gerarRotinaConsolidada,
  gerarTextoProtocoloMixes,
  gerarTextoRotinaBeEstar,
  gerarTextoProtocoloTouch,
  gerarTextoProtocoloPersonalizado,
  PROTOCOLOS_KIDS,
  type FaixaEtariaKids,
  type ProtocoloItemResolvido,
} from '@/utils/protocoloKids'

interface Props {
  nomeCrianca: string
  faixaEtaria: FaixaEtariaKids
  mixesIndicados: string[]
  oleosTouchInd: string[]
  // Para Protocolo Personalizado: demandas especiais da criança (TDAH, autismo, etc.)
  demandasEspeciais?: string[]
}

// ──────────────────────────────────────────────
// Sub-componente: card individual de um óleo
// ──────────────────────────────────────────────
function ItemProtocolo({ item }: { item: ProtocoloItemResolvido }) {
  return (
    <div className="bg-white border border-[#EDE0D4] rounded-xl p-4">
      <div className="flex items-start gap-3 mb-3">
        <span className="text-2xl">{item.emoji}</span>
        <div className="flex-1">
          <div className="font-semibold text-[#2D6A4F] text-base">{item.nomeExibicao}</div>
          {item.evitarLuzSolar && (
            <div className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 bg-[#FEF3C7] border border-[#FCD34D] rounded-md text-[11px] text-[#854F0B] font-medium">
              ⚠️ Evitar luz solar por 12h após aplicação
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2.5">
        <div>
          <div className="text-xs font-semibold text-[#B08968] uppercase tracking-wide mb-1">Locais de aplicação</div>
          <div className="flex flex-wrap gap-1.5">
            {item.locais.map((l, i) => (
              <span key={i} className="px-2.5 py-1 bg-[#D8F3DC] text-[#2D6A4F] rounded-full text-xs">
                → {l}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold text-[#B08968] uppercase tracking-wide mb-1">Rotina sugerida</div>
          <div className="space-y-1.5">
            {item.rotinas.map((r, i) => (
              <div key={i} className="bg-[#F8F4EF] rounded-lg px-3 py-2">
                <div className="text-xs font-semibold text-[#2D6A4F] mb-0.5">{r.titulo}</div>
                <div className="text-sm text-[#2C2C2A]">{r.texto}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-[#5F5E5A]">
          <span className="font-semibold">Frequência:</span> {item.frequencia}
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Sub-componente: card de período (manhã/tarde/noite)
// ──────────────────────────────────────────────
function PeriodoCard({
  titulo,
  emoji,
  lista,
  cor,
  fundo,
}: {
  titulo: string
  emoji: string
  lista: ProtocoloItemResolvido[]
  cor: string
  fundo: string
}) {
  return (
    <div className="rounded-xl border-2 p-4" style={{ borderColor: cor, backgroundColor: fundo }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{emoji}</span>
        <div className="font-semibold uppercase tracking-wide text-sm" style={{ color: cor }}>
          {titulo}
        </div>
      </div>
      {lista.length === 0 ? (
        <div className="text-xs italic text-[#5F5E5A]">Sem aplicações neste período.</div>
      ) : (
        <ul className="space-y-1.5">
          {lista.map(i => (
            <li key={i.chave} className="text-sm text-[#2C2C2A]">
              <span className="font-medium">
                → {i.emoji} {i.nomeExibicao}
              </span>
              <span className="text-[#5F5E5A]">
                : aplicar em {i.locais.map(l => l.toLowerCase()).join(', ')}
                {i.finalidadeCurta ? ` — ${i.finalidadeCurta}` : ''}
              </span>
              {i.evitarLuzSolar && (
                <span className="block text-[11px] text-[#854F0B] mt-0.5">⚠️ Evitar luz solar por 12h</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────
// Sub-componente: botão de copiar
// ──────────────────────────────────────────────
function BotaoCopiar({
  label,
  labelCopiado,
  onCopiar,
  variant = 'primary',
}: {
  label: string
  labelCopiado?: string
  onCopiar: () => Promise<void>
  variant?: 'primary' | 'secondary'
}) {
  const [copiado, setCopiado] = useState(false)
  const handleClick = async () => {
    await onCopiar()
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2000)
  }
  const base = 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all'
  const cls =
    variant === 'primary'
      ? copiado
        ? `${base} bg-[#D8F3DC] text-[#2D6A4F]`
        : `${base} bg-[#2D6A4F] text-white hover:bg-[#245A42]`
      : copiado
      ? `${base} bg-[#D8F3DC] text-[#2D6A4F] border border-[#A8DFC4]`
      : `${base} bg-white text-[#2D6A4F] border border-[#2D6A4F] hover:bg-[#D8F3DC]`
  return (
    <button onClick={handleClick} className={cls}>
      <Copy className="w-4 h-4" />
      {copiado ? (labelCopiado || 'Copiado!') : label}
    </button>
  )
}

// ──────────────────────────────────────────────
// Lista de todos os óleos disponíveis p/ protocolo personalizado
// ──────────────────────────────────────────────
const TODOS_MIXES_KIDS = ['CALMER', 'THINKER', 'BRAVE', 'STEADY', 'STRONGER', 'TAMER', 'RESCUER']
const TODOS_TOUCH_KIDS = [
  'LAVANDA_TOUCH',
  'VETIVER_TOUCH',
  'BREATHE_TOUCH',
  'ON_GUARD_TOUCH',
  'ADAPTIV_TOUCH',
  'HORTELA_TOUCH',
  'ZENGEST_TOUCH',
]

// ──────────────────────────────────────────────
// Sub-componente: Protocolo Personalizado
// ──────────────────────────────────────────────
function ProtocoloPersonalizado({
  nomeCrianca,
  faixaEtaria,
  demandasEspeciais = [],
}: {
  nomeCrianca: string
  faixaEtaria: FaixaEtariaKids
  demandasEspeciais?: string[]
}) {
  const [mixesSel, setMixesSel] = useState<string[]>([])
  const [touchSel, setTouchSel] = useState<string[]>([])
  const [gerado, setGerado] = useState(false)
  const [itensGerados, setItensGerados] = useState<ProtocoloItemResolvido[]>([])

  const ehAdolescente = faixaEtaria === 'ADOLESCENTE'

  // Verifica se tem demanda especial para Vetiver
  const temDemandasVetiver = demandasEspeciais.some(d => {
    const dLower = d.toLowerCase()
    return (
      dLower.includes('tdah') ||
      dLower.includes('autismo') ||
      dLower.includes('hiperatividade') ||
      dLower.includes('déficit de atenção') ||
      dLower.includes('deficit de atencao')
    )
  })

  // Verifica se um óleo touch está habilitado para a faixa etária
  const touchHabilitado = (chave: string): { ok: boolean; motivo?: string } => {
    const p = PROTOCOLOS_KIDS[chave]
    if (!p) return { ok: false, motivo: 'Óleo não encontrado' }
    if (p.apenasAdolescenteEspecial) {
      if (!ehAdolescente) return { ok: false, motivo: 'Não indicado para esta faixa etária' }
      if (!temDemandasVetiver) return { ok: false, motivo: 'Indicado apenas para adolescentes com TDAH, autismo, hiperatividade ou déficit de atenção' }
    }
    if (p.apenasAdolescente && !ehAdolescente) {
      return { ok: false, motivo: 'Não indicado para esta faixa etária' }
    }
    return { ok: true }
  }

  const toggleMix = (chave: string) => {
    setGerado(false)
    setMixesSel(prev => prev.includes(chave) ? prev.filter(c => c !== chave) : [...prev, chave])
  }

  const toggleTouch = (chave: string) => {
    const hab = touchHabilitado(chave)
    if (!hab.ok) return
    setGerado(false)
    setTouchSel(prev => prev.includes(chave) ? prev.filter(c => c !== chave) : [...prev, chave])
  }

  const gerarProtocolo = () => {
    // mixesSel e touchSel já contêm as CHAVES (ex.: 'CALMER', 'LAVANDA_TOUCH'),
    // que resolverChave/resolverProtocolos reconhecem diretamente em PROTOCOLOS_KIDS.
    // Passamos as chaves sem conversão para evitar perda dos mixes (que não estavam no ALIAS).
    const itens = resolverProtocolos(mixesSel, touchSel, faixaEtaria)
    setItensGerados(itens)
    setGerado(true)
  }

  const totalSelecionados = mixesSel.length + touchSel.length

  return (
    <div className="space-y-5">
      <div className="text-sm text-[#5F5E5A]">
        Selecione os óleos que <strong>{nomeCrianca || 'a criança'}</strong> vai usar para gerar o protocolo personalizado:
      </div>

      {/* Mixes Kids */}
      <div>
        <div className="text-xs font-semibold text-[#2D6A4F] uppercase tracking-wide mb-3">🌿 Mixes Kids</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TODOS_MIXES_KIDS.map(chave => {
            const p = PROTOCOLOS_KIDS[chave]
            if (!p) return null
            const sel = mixesSel.includes(chave)
            return (
              <label
                key={chave}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  sel
                    ? 'border-[#2D6A4F] bg-[#D8F3DC]'
                    : 'border-[#EDE0D4] bg-white hover:border-[#52B788]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={sel}
                  onChange={() => toggleMix(chave)}
                  className="w-4 h-4 accent-[#2D6A4F]"
                />
                <span className="text-lg">{p.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#2C2C2A] leading-tight">{p.nomeExibicao}</div>
                  <div className="text-xs text-[#5F5E5A]">dōTERRA {chave.charAt(0) + chave.slice(1).toLowerCase()}</div>
                </div>
              </label>
            )
          })}
        </div>
      </div>

      {/* Óleos Touch */}
      <div>
        <div className="text-xs font-semibold text-[#2D6A4F] uppercase tracking-wide mb-3">💧 Óleos Touch</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TODOS_TOUCH_KIDS.map(chave => {
            const p = PROTOCOLOS_KIDS[chave]
            if (!p) return null
            const hab = touchHabilitado(chave)
            const sel = touchSel.includes(chave)
            return (
              <label
                key={chave}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                  !hab.ok
                    ? 'border-[#EDE0D4] bg-[#F3F4F6] opacity-60 cursor-not-allowed'
                    : sel
                    ? 'border-[#2D6A4F] bg-[#D8F3DC] cursor-pointer'
                    : 'border-[#EDE0D4] bg-white hover:border-[#52B788] cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  checked={sel}
                  disabled={!hab.ok}
                  onChange={() => toggleTouch(chave)}
                  className="w-4 h-4 accent-[#2D6A4F]"
                />
                <span className="text-lg">{p.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium leading-tight ${!hab.ok ? 'text-[#9CA3AF]' : 'text-[#2C2C2A]'}`}>
                    {p.nomeExibicao}
                  </div>
                  {!hab.ok ? (
                    <div className="text-[11px] text-[#9CA3AF] italic mt-0.5">{hab.motivo}</div>
                  ) : (
                    <div className="text-xs text-[#5F5E5A]">{p.apenasAdolescente || p.apenasAdolescenteEspecial ? 'Adolescente' : 'Todas as faixas'}</div>
                  )}
                </div>
              </label>
            )
          })}
        </div>
      </div>

      {/* Botão gerar */}
      <button
        onClick={gerarProtocolo}
        disabled={totalSelecionados === 0}
        className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all ${
          totalSelecionados === 0
            ? 'bg-[#EDE0D4] text-[#B08968] cursor-not-allowed'
            : 'bg-[#2D6A4F] text-white hover:bg-[#245A42]'
        }`}
      >
        <Wand2 className="w-4 h-4" />
        Gerar protocolo personalizado
        {totalSelecionados > 0 && ` (${totalSelecionados} óleo${totalSelecionados > 1 ? 's' : ''} selecionado${totalSelecionados > 1 ? 's' : ''})`}
      </button>

      {/* Resultado gerado */}
      {gerado && itensGerados.length > 0 && (
        <div className="space-y-4 mt-2">
          <div className="text-sm font-semibold text-[#2D6A4F]">✅ Protocolo gerado para {nomeCrianca || 'a criança'}:</div>

          {/* Aviso segurança */}
          {(() => {
            const aviso = obterAvisoSeguranca(faixaEtaria)
            return (
              <div
                className={`rounded-xl border-2 p-4 ${
                  aviso.tipo === 'amarelo'
                    ? 'bg-[#FEF3C7] border-[#FCD34D] text-[#854F0B]'
                    : 'bg-[#D8F3DC] border-[#52B788] text-[#2D6A4F]'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-xl leading-none">{aviso.icone}</span>
                  <div>
                    <div className="font-semibold text-sm mb-0.5">{aviso.titulo}</div>
                    <div className="text-sm">{aviso.texto}</div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Itens */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {itensGerados.map(item => (
              <ItemProtocolo key={item.chave} item={item} />
            ))}
          </div>

          {/* Rotina consolidada */}
          {(() => {
            const { manha, tarde, noite, conformeNecessidade } = gerarRotinaConsolidada(itensGerados)
            return (
              <div>
                <div className="text-sm font-semibold text-[#2D6A4F] mb-3">
                  🌿 Rotina de {nomeCrianca || 'a criança'}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <PeriodoCard titulo="Manhã" emoji="☀️" lista={manha} cor="#F59E0B" fundo="#FFFBEB" />
                  <PeriodoCard titulo="Tarde" emoji="🌤️" lista={tarde} cor="#52B788" fundo="#F0FAF4" />
                  <PeriodoCard titulo="Noite" emoji="🌙" lista={noite} cor="#7C3AED" fundo="#F5F3FF" />
                </div>
                {conformeNecessidade.length > 0 && (
                  <div className="mt-3">
                    <PeriodoCard
                      titulo="Conforme necessidade"
                      emoji="🔁"
                      lista={conformeNecessidade}
                      cor="#B08968"
                      fundo="#F8F4EF"
                    />
                  </div>
                )}
              </div>
            )
          })()}

          {/* Botão copiar personalizado */}
          <BotaoCopiar
            label="Copiar protocolo personalizado"
            onCopiar={async () => {
              const texto = gerarTextoProtocoloPersonalizado(nomeCrianca || 'a criança', faixaEtaria, itensGerados)
              await copiarTexto(texto)
              toast.success('Protocolo personalizado copiado!')
            }}
          />
        </div>
      )}

      {gerado && itensGerados.length === 0 && (
        <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-xl p-4 text-sm text-[#854F0B]">
          ⚠️ Nenhum protocolo disponível para a combinação selecionada nesta faixa etária.
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────
// Componente principal
// ──────────────────────────────────────────────
export default function BlocoProtocoloUso({ nomeCrianca, faixaEtaria, mixesIndicados, oleosTouchInd, demandasEspeciais = [] }: Props) {
  const [blocoAberto, setBlocoAberto] = useState<'mixes' | 'touch' | 'personalizado' | null>('mixes')

  const aviso = obterAvisoSeguranca(faixaEtaria)
  const itensMixes = resolverProtocolos(mixesIndicados, [], faixaEtaria)
  const itensTouch = resolverProtocolos([], oleosTouchInd, faixaEtaria)
  const todosItens = resolverProtocolos(mixesIndicados, oleosTouchInd, faixaEtaria)

  const {
    manha: manhaMixes,
    tarde: tardeMixes,
    noite: noiteMixes,
    conformeNecessidade: conformeNecessidadeMixes,
  } = gerarRotinaConsolidada(itensMixes)

  const temMixes = itensMixes.length > 0
  const temTouch = itensTouch.length > 0

  const toggle = (bloco: 'mixes' | 'touch' | 'personalizado') => {
    setBlocoAberto(prev => (prev === bloco ? null : bloco))
  }

  // ── Cabeçalho de seção colapsável
  const SecaoHeader = ({
    id,
    titulo,
    badge,
    cor,
  }: {
    id: 'mixes' | 'touch' | 'personalizado'
    titulo: string
    badge?: string
    cor?: string
  }) => (
    <button
      onClick={() => toggle(id)}
      className="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all text-left"
      style={{ backgroundColor: cor || '#F0FAF4', border: `2px solid ${cor ? cor + '88' : '#A8DFC4'}` }}
    >
      <div className="flex items-center gap-2">
        <span className="font-semibold text-[#2D6A4F] text-sm">{titulo}</span>
        {badge && (
          <span className="px-2 py-0.5 bg-[#2D6A4F] text-white rounded-full text-[11px] font-medium">{badge}</span>
        )}
      </div>
      {blocoAberto === id ? (
        <ChevronUp className="w-4 h-4 text-[#2D6A4F]" />
      ) : (
        <ChevronDown className="w-4 h-4 text-[#2D6A4F]" />
      )}
    </button>
  )

  return (
    <div className="space-y-4">
      {/* Aviso de segurança global */}
      <div
        className={`rounded-xl border-2 p-4 ${
          aviso.tipo === 'amarelo'
            ? 'bg-[#FEF3C7] border-[#FCD34D] text-[#854F0B]'
            : 'bg-[#D8F3DC] border-[#52B788] text-[#2D6A4F]'
        }`}
      >
        <div className="flex items-start gap-2">
          <span className="text-xl leading-none">{aviso.icone}</span>
          <div>
            <div className="font-semibold text-sm mb-0.5">{aviso.titulo}</div>
            <div className="text-sm">{aviso.texto}</div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          BLOCO A — Protocolo dos Mixes
          ════════════════════════════════════════ */}
      <div className="rounded-2xl border-2 border-[#A8DFC4] overflow-hidden">
        <SecaoHeader
          id="mixes"
          titulo="🌿 Bloco A — Protocolo dos Mixes"
          badge={temMixes ? `${itensMixes.length} mix${itensMixes.length > 1 ? 'es' : ''}` : undefined}
        />
        {blocoAberto === 'mixes' && (
          <div className="p-4 space-y-4">
            {!temMixes ? (
              <div className="bg-[#F8F4EF] border-2 border-dashed border-[#EDE0D4] rounded-xl p-5 text-center">
                <div className="text-sm text-[#5F5E5A] italic">Nenhum mix kids indicado pelo diagnóstico.</div>
              </div>
            ) : (
              <>
                {/* Protocolos individuais */}
                <div>
                  <div className="text-sm font-semibold text-[#2D6A4F] mb-3">Protocolo individual dos mixes</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {itensMixes.map(m => (
                      <ItemProtocolo key={m.chave} item={m} />
                    ))}
                  </div>
                </div>

                {/* Rotina consolidada dos mixes */}
                <div>
                  <div className="text-sm font-semibold text-[#2D6A4F] mb-3">
                    🌿 Rotina de bem-estar de {nomeCrianca || 'a criança'}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <PeriodoCard titulo="Manhã" emoji="☀️" lista={manhaMixes} cor="#F59E0B" fundo="#FFFBEB" />
                    <PeriodoCard titulo="Tarde" emoji="🌤️" lista={tardeMixes} cor="#52B788" fundo="#F0FAF4" />
                    <PeriodoCard titulo="Noite" emoji="🌙" lista={noiteMixes} cor="#7C3AED" fundo="#F5F3FF" />
                    {conformeNecessidadeMixes.length > 0 && (
                      <PeriodoCard
                        titulo="Conforme necessidade"
                        emoji="🔁"
                        lista={conformeNecessidadeMixes}
                        cor="#B08968"
                        fundo="#F8F4EF"
                      />
                    )}
                  </div>
                </div>

                {/* Dois botões */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <BotaoCopiar
                    label="Copiar protocolo dos mixes"
                    onCopiar={async () => {
                      const texto = gerarTextoProtocoloMixes(nomeCrianca || 'a criança', faixaEtaria, itensMixes)
                      await copiarTexto(texto)
                      toast.success('Protocolo dos mixes copiado!')
                    }}
                  />
                  <BotaoCopiar
                    label="Copiar rotina de bem-estar"
                    variant="secondary"
                    onCopiar={async () => {
                      const texto = gerarTextoRotinaBeEstar(nomeCrianca || 'a criança', faixaEtaria, itensMixes)
                      await copiarTexto(texto)
                      toast.success('Rotina de bem-estar copiada!')
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════
          BLOCO B — Protocolo Touch
          ════════════════════════════════════════ */}
      <div className="rounded-2xl border-2 border-[#7AB8E8] overflow-hidden">
        <button
          onClick={() => toggle('touch')}
          className="w-full flex items-center justify-between px-4 py-3 bg-[#EFF7FF] text-left transition-all"
          style={{ borderBottom: blocoAberto === 'touch' ? '2px solid #7AB8E8' : 'none' }}
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[#1D6FA4] text-sm">💧 Bloco B — Protocolo Touch</span>
            {temTouch && (
              <span className="px-2 py-0.5 bg-[#1D6FA4] text-white rounded-full text-[11px] font-medium">
                {itensTouch.length} óleo{itensTouch.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          {blocoAberto === 'touch' ? (
            <ChevronUp className="w-4 h-4 text-[#1D6FA4]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#1D6FA4]" />
          )}
        </button>
        {blocoAberto === 'touch' && (
          <div className="p-4 space-y-4">
            {!temTouch ? (
              <div className="bg-[#F8F4EF] border-2 border-dashed border-[#EDE0D4] rounded-xl p-5 text-center">
                <div className="text-sm text-[#5F5E5A] italic">Nenhum óleo touch indicado pelo diagnóstico.</div>
              </div>
            ) : (
              <>
                <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-3 py-2 text-xs text-[#854F0B]">
                  💡 Os óleos touch são diluídos e prontos para usar diretamente na pele. Além de indicados para {nomeCrianca || 'a criança'}, podem ser usados por toda a família.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {itensTouch.map(t => (
                    <ItemProtocolo key={t.chave} item={t} />
                  ))}
                </div>

                {/* Botão copiar touch */}
                <BotaoCopiar
                  label="Copiar protocolo touch"
                  onCopiar={async () => {
                    const texto = gerarTextoProtocoloTouch(nomeCrianca || 'a criança', itensTouch)
                    await copiarTexto(texto)
                    toast.success('Protocolo touch copiado!')
                  }}
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════
          BLOCO C — Protocolo Personalizado
          ════════════════════════════════════════ */}
      <div className="rounded-2xl border-2 border-[#B08968] overflow-hidden">
        <button
          onClick={() => toggle('personalizado')}
          className="w-full flex items-center justify-between px-4 py-3 bg-[#FAF6F0] text-left transition-all"
          style={{ borderBottom: blocoAberto === 'personalizado' ? '2px solid #B08968' : 'none' }}
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[#7C4A1E] text-sm">✨ Bloco C — Protocolo Personalizado</span>
            <span className="px-2 py-0.5 bg-[#B08968] text-white rounded-full text-[11px] font-medium">Pós-venda</span>
          </div>
          {blocoAberto === 'personalizado' ? (
            <ChevronUp className="w-4 h-4 text-[#7C4A1E]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#7C4A1E]" />
          )}
        </button>
        {blocoAberto === 'personalizado' && (
          <div className="p-4">
            <div className="text-xs text-[#5F5E5A] italic mb-4">
              Selecione apenas os óleos que a mãe adquiriu para gerar o protocolo exato dos produtos comprados.
            </div>
            <ProtocoloPersonalizado
              nomeCrianca={nomeCrianca}
              faixaEtaria={faixaEtaria}
              demandasEspeciais={demandasEspeciais}
            />
          </div>
        )}
      </div>
    </div>
  )
}
